<template>
  <div class="warehouse-form">
    <div class="card" style="max-width: 980px;">
      <div v-if="damage" class="form-grid">
        <div class="card-header">
          <div>
            <h2 class="title" style="font-size: 22px;">Detalhe da avaria</h2>
            <p class="subtitle">Status: {{ damage.status }}</p>
          </div>
        </div>

        <div class="item-card item-summary">
          <div class="item-photo-wrap">
            <img v-if="item?.photoDataUrl" class="item-photo" :src="$resolveUrl(item?.photoDataUrl)" alt="foto item" />
            <div v-else class="item-photo item-photo-empty">Sem foto</div>
          </div>
          <div>
            <strong>{{ item?.name || damage.toolItemId }}</strong>
            <div class="item-meta">ID: {{ item?.code || damage.toolItemId }}</div>
            <div class="item-meta">Resumo: {{ damage.damageSummary || '-' }}</div>
          </div>
        </div>

        <div class="item-card">
          <div class="item-meta"><strong>Projeto:</strong> {{ damage.projectId || '-' }}</div>
          <div class="item-meta"><strong>Colaborador:</strong> {{ damage.collaboratorUsername || '-' }}</div>
          <div class="item-meta"><strong>Descricao:</strong> {{ damage.damageDetails || damage.description || '-' }}</div>
        </div>

        <div v-if="damage.photoDataUrls && damage.photoDataUrls.length > 0" class="image-grid">
          <div v-for="(img, index) in damage.photoDataUrls" :key="`damage-detail-${index}`" class="image-preview">
            <img :src="resolveUrl(img)" alt="foto avaria" />
          </div>
        </div>

        <div v-if="canResolve && damage.status !== 'resolvido'" class="item-card">
          <button v-if="!showResolveForm" class="btn btn-primary" type="button" @click="showResolveForm = true">
            Foi solucionado
          </button>
          <div v-else class="form-grid">
            <div class="input-field">
              <label for="resolution-notes">Descricao do que foi feito</label>
              <textarea id="resolution-notes" v-model="resolutionNotes" rows="4"></textarea>
            </div>
            <div class="actions">
              <button class="btn btn-primary" type="button" @click="resolveDamage">Salvar</button>
              <button class="btn btn-ghost" type="button" @click="cancelResolve">Cancelar</button>
            </div>
          </div>
        </div>

        <div class="actions">
          <button class="btn btn-ghost" type="button" @click="goBack">Voltar</button>
        </div>
      </div>
      <div v-else class="hint">Avaria nao encontrada.</div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { navigationStore } from '@/store/navigation'
import { apiFetch } from '@/store/api'
import { warehouseStore } from '@/store/warehouse'
import { resolveUrl } from '@/utils/resolveUrl'

interface DamageTicket {
  id: string
  toolItemId: string
  description: string
  damageSummary: string
  damageDetails: string
  collaboratorUsername: string
  projectId: string
  photoDataUrls: string[]
  status: string
}

export default defineComponent({
  name: 'WarehouseDamageDetailView',
  props: {
    canResolve: { type: Boolean, default: false }
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const damage = ref<DamageTicket | null>(null)
    const showResolveForm = ref(false)
    const resolutionNotes = ref('')

    const item = computed(() => warehouseStore.all().find((entry) => entry.id === (damage.value?.toolItemId || '')) || null)

    const loadDamage = async () => {
      const damageId = typeof route.params.id === 'string' ? route.params.id : ''
      if (!damageId) return
      const rows = await apiFetch<DamageTicket[]>('/tickets?category=manutencao-ferramentas')
      damage.value = rows.find((row) => row.id === damageId) || null
    }

    const resolveDamage = async () => {
      if (!damage.value || !resolutionNotes.value.trim()) return
      await apiFetch(`/tickets/${encodeURIComponent(damage.value.id)}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolutionNotes: resolutionNotes.value.trim() })
      })
      await router.push('/almoxarifado/avarias')
    }

    const cancelResolve = () => {
      showResolveForm.value = false
      resolutionNotes.value = ''
    }

    const goBack = () => {
      navigationStore.goBack(router, '/almoxarifado/avarias')
    }

    onMounted(() => {
      Promise.all([warehouseStore.refresh(), loadDamage()]).catch(() => {
        // ignore bootstrap errors
      })
    })

    return {
      damage,
      item,
      showResolveForm,
      resolutionNotes,
      resolveUrl,
      resolveDamage,
      cancelResolve,
      goBack
    }
  }
})
</script>

<style scoped lang="scss">
.warehouse-form {
  width: 100%;
}

.item-summary {
  grid-template-columns: 120px 1fr;
}

.item-photo-wrap {
  width: 110px;
  height: 110px;
  overflow: hidden;
}

.item-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.item-photo-empty {
  display: grid;
  place-items: center;
  background: #ececec;
  color: #565656;
  font-size: 12px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}

.image-preview {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(31, 30, 26, 0.14);
  aspect-ratio: 1 / 1;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
